import type { EntityId, Percent, SceneId } from "./types";

/** Типизированный реестр игровых событий. */
export interface GameEvents {
  "seed:collected": { id: EntityId; remaining: number };
  "sand:collected": { id: EntityId; energyDelta: Percent };
  "flower:used": { id: EntityId; energyDelta: Percent };
  "glowfly:collected": { id: EntityId; energyDelta: Percent };
  "energy:depleted": null;
  "energy:full": null;
  "calm-spot:enter": null;
  "calm-spot:exit": null;
  "garden-center:reached": null;
  "level:complete": null;
  "glow:start": null;
  "glow:stop": null;
  "scene:change": { from: SceneId; to: SceneId };
}

export type EventName<E> = keyof E & string;
export type EventHandler<P> = (payload: P) => void;
export type Unsubscribe = () => void;

/** Универсальный типизированный pub/sub. E — реестр событий вида { name: payload }. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class EventBus<E extends Record<keyof E, any>> {
  private handlers = new Map<EventName<E>, Set<EventHandler<unknown>>>();

  /** Подписывается на событие, возвращает функцию отписки. */
  on<K extends EventName<E>>(event: K, handler: EventHandler<E[K]>): Unsubscribe {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as EventHandler<unknown>);
    return () => {
      set?.delete(handler as EventHandler<unknown>);
    };
  }

  /** Отписывается от события. */
  off<K extends EventName<E>>(event: K, handler: EventHandler<E[K]>): void {
    this.handlers.get(event)?.delete(handler as EventHandler<unknown>);
  }

  /** Рассылает событие всем подписчикам. */
  emit<K extends EventName<E>>(event: K, payload: E[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const handler of set) (handler as EventHandler<E[K]>)(payload);
  }

  /** Удаляет все подписчики (используется при teardown). */
  clear(): void {
    this.handlers.clear();
  }
}

/** Фабрика игрового bus — тесты создают изолированный экземпляр. */
export const createGameBus = (): EventBus<GameEvents> => new EventBus<GameEvents>();
